import { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { 
  Lock, CheckCircle2, XCircle, Clock, AlertTriangle, Zap,
  Loader2, User, Calendar, MessageSquare, ChevronDown, ChevronUp
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';

const URGENCY_CONFIG = {
  low: { label: 'Low', color: 'bg-gray-100 text-gray-600', icon: Clock },
  normal: { label: 'Normal', color: 'bg-[#4A3B32]/10 text-[#4A3B32]', icon: Clock },
  high: { label: 'High', color: 'bg-amber-100 text-amber-700', icon: AlertTriangle },
  urgent: { label: 'Urgent', color: 'bg-red-100 text-red-700', icon: Zap },
};

const STATUS_CONFIG = {
  pending: { label: 'Pending Review', color: 'bg-amber-100 text-amber-700', icon: Clock },
  approved: { label: 'Approved', color: 'bg-green-100 text-green-700', icon: CheckCircle2 },
  denied: { label: 'Denied', color: 'bg-red-100 text-red-700', icon: XCircle },
};

function formatDate(dateString) {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-AU', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
}

function RequestCard({ request, onApprove, onDeny }) {
  const [expanded, setExpanded] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [showDecisionModal, setShowDecisionModal] = useState(null); // 'approve' | 'deny' | null
  const [adminNotes, setAdminNotes] = useState('');

  const urgencyConfig = URGENCY_CONFIG[request.urgency] || URGENCY_CONFIG.normal;
  const statusConfig = STATUS_CONFIG[request.status] || STATUS_CONFIG.pending;
  const UrgencyIcon = urgencyConfig.icon;
  const StatusIcon = statusConfig.icon;

  const handleDecision = async (status) => {
    setProcessing(true);
    try {
      if (status === 'approved') {
        await onApprove(request.id, adminNotes);
      } else {
        await onDeny(request.id, adminNotes);
      }
      setShowDecisionModal(null);
      setAdminNotes('');
    } catch (error) {
      // Error handled in parent
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div className={cn(
      "border rounded-lg overflow-hidden transition-all",
      request.status === 'pending' ? "border-amber-200 bg-amber-50/30" : "border-[#E6E0D9] bg-white"
    )}>
      {/* Header */}
      <div 
        className="p-4 cursor-pointer hover:bg-[#F9F7F5]/50"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <Lock className="w-4 h-4 text-[#7D8C9E]" />
              <span className="font-semibold text-[#2C2420] truncate">{request.clientName}</span>
            </div>
            <div className="flex items-center gap-3 text-sm text-[#5D524D]">
              <span className="flex items-center gap-1">
                <User className="w-3 h-3" />
                {request.requesterName}
              </span>
              <span className="flex items-center gap-1">
                <Calendar className="w-3 h-3" />
                {formatDate(request.createdAt)}
              </span>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <span className={cn("px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1", urgencyConfig.color)}>
              <UrgencyIcon className="w-3 h-3" />
              {urgencyConfig.label}
            </span>
            <span className={cn("px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1", statusConfig.color)}>
              <StatusIcon className="w-3 h-3" />
              {statusConfig.label}
            </span>
            {expanded ? <ChevronUp className="w-4 h-4 text-[#8C8078]" /> : <ChevronDown className="w-4 h-4 text-[#8C8078]" />}
          </div>
        </div>
      </div>

      {/* Expanded Content */}
      {expanded && (
        <div className="px-4 pb-4 pt-2 border-t border-[#E6E0D9]">
          <div className="space-y-4">
            {/* Reason */}
            <div>
              <p className="text-xs font-medium text-[#8C8078] uppercase tracking-wide mb-1">Reason for Access</p>
              <p className="text-sm text-[#2C2420] bg-[#F9F7F5] p-3 rounded-lg">{request.reason}</p>
            </div>

            {/* Requester Info */}
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-xs font-medium text-[#8C8078] uppercase tracking-wide mb-1">Email</p>
                <p className="text-[#2C2420]">{request.requesterEmail}</p>
              </div>
              <div>
                <p className="text-xs font-medium text-[#8C8078] uppercase tracking-wide mb-1">Request ID</p>
                <p className="text-[#2C2420] font-mono text-xs">{request.id}</p>
              </div>
            </div>

            {/* Review Info (if already processed) */}
            {request.status !== 'pending' && (
              <div className="p-3 bg-[#F9F7F5] rounded-lg">
                <p className="text-xs font-medium text-[#8C8078] uppercase tracking-wide mb-2">Decision</p>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-[#8C8078]">Reviewed by</p>
                    <p className="text-[#2C2420]">{request.reviewedBy || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-[#8C8078]">Reviewed at</p>
                    <p className="text-[#2C2420]">{formatDate(request.reviewedAt)}</p>
                  </div>
                </div>
                {request.adminNotes && (
                  <div className="mt-3">
                    <p className="text-[#8C8078]">Notes</p>
                    <p className="text-[#2C2420] italic">"{request.adminNotes}"</p>
                  </div>
                )}
              </div>
            )}

            {/* Action Buttons (for pending requests) */}
            {request.status === 'pending' && (
              <div className="flex justify-end gap-2 pt-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowDecisionModal('deny')}
                  className="text-red-600 border-red-200 hover:bg-red-50"
                  data-testid={`deny-request-${request.id}`}
                >
                  <XCircle className="w-4 h-4 mr-1" />
                  Deny
                </Button>
                <Button
                  size="sm"
                  onClick={() => setShowDecisionModal('approve')}
                  className="bg-green-600 hover:bg-green-700"
                  data-testid={`approve-request-${request.id}`}
                >
                  <CheckCircle2 className="w-4 h-4 mr-1" />
                  Approve
                </Button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Decision Modal */}
      <Dialog open={showDecisionModal !== null} onOpenChange={() => setShowDecisionModal(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {showDecisionModal === 'approve' ? (
                <>
                  <CheckCircle2 className="w-5 h-5 text-green-600" />
                  Approve Access Request
                </>
              ) : (
                <>
                  <XCircle className="w-5 h-5 text-red-600" />
                  Deny Access Request
                </>
              )}
            </DialogTitle>
            <DialogDescription>
              {showDecisionModal === 'approve'
                ? `Grant ${request.requesterName} access to ${request.clientName}`
                : `Deny ${request.requesterName}'s request to access ${request.clientName}`}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label htmlFor="admin-notes">Notes (optional)</Label>
              <Textarea
                id="admin-notes"
                placeholder="Add any notes for the requester..."
                value={adminNotes}
                onChange={(e) => setAdminNotes(e.target.value)}
                className="min-h-[80px]"
              />
            </div>

            <div className="flex justify-end gap-3 pt-2">
              <Button
                variant="outline"
                onClick={() => setShowDecisionModal(null)}
                disabled={processing}
              >
                Cancel
              </Button>
              <Button
                onClick={() => handleDecision(showDecisionModal === 'approve' ? 'approved' : 'denied')}
                disabled={processing}
                className={showDecisionModal === 'approve' ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'}
              >
                {processing ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : showDecisionModal === 'approve' ? (
                  'Approve'
                ) : (
                  'Deny'
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function AccessRequestsManager() {
  const { token, isOrgAdmin } = useAuth();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending'); // 'all', 'pending', 'approved', 'denied'

  useEffect(() => {
    fetchRequests();
  }, [token, filter]);

  const fetchRequests = async () => {
    if (!token) return;
    
    setLoading(true);
    try {
      const statusParam = filter === 'all' ? null : filter;
      const response = await api.getAccessRequests(token, statusParam);
      setRequests(response.data.requests || []);
    } catch (error) {
      console.error('Failed to fetch access requests:', error);
      toast.error('Failed to load access requests');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (requestId, notes) => {
    try {
      await api.updateAccessRequest(token, requestId, { status: 'approved', adminNotes: notes });
      toast.success('Access request approved');
      fetchRequests();
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to approve request';
      toast.error(message);
      throw error;
    }
  };

  const handleDeny = async (requestId, notes) => {
    try {
      await api.updateAccessRequest(token, requestId, { status: 'denied', adminNotes: notes });
      toast.success('Access request denied');
      fetchRequests();
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to deny request';
      toast.error(message);
      throw error;
    }
  };

  if (!isOrgAdmin) {
    return (
      <div className="p-8 text-center">
        <Lock className="w-12 h-12 text-[#8C8078] mx-auto mb-4" />
        <h3 className="text-lg font-medium text-[#2C2420] mb-2">Admin Access Required</h3>
        <p className="text-[#5D524D]">You need administrator privileges to manage access requests.</p>
      </div>
    );
  }

  const pendingCount = requests.filter(r => r.status === 'pending').length;

  return (
    <div className="space-y-6" data-testid="access-requests-manager">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-[#2C2420]">Access Requests</h3>
          <p className="text-sm text-[#5D524D]">Manage requests for restricted client access</p>
        </div>
        {pendingCount > 0 && (
          <div className="px-3 py-1 bg-amber-100 text-amber-700 rounded-full text-sm font-medium">
            {pendingCount} pending
          </div>
        )}
      </div>

      {/* Filter Tabs */}
      <div className="flex gap-1 bg-[#F5F3F1] rounded-lg p-1">
        {[
          { value: 'pending', label: 'Pending' },
          { value: 'approved', label: 'Approved' },
          { value: 'denied', label: 'Denied' },
          { value: 'all', label: 'All' },
        ].map((tab) => (
          <button
            key={tab.value}
            onClick={() => setFilter(tab.value)}
            className={cn(
              "flex-1 px-4 py-2 rounded-md text-sm font-medium transition-all",
              filter === tab.value
                ? "bg-white text-[#2C2420] shadow-sm"
                : "text-[#5D524D] hover:text-[#2C2420]"
            )}
            data-testid={`filter-${tab.value}`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Requests List */}
      {loading ? (
        <div className="py-12 text-center">
          <Loader2 className="w-8 h-8 animate-spin mx-auto text-[#4A3B32]" />
          <p className="text-sm text-[#5D524D] mt-2">Loading requests...</p>
        </div>
      ) : requests.length === 0 ? (
        <div className="py-12 text-center border border-dashed border-[#E6E0D9] rounded-lg">
          <MessageSquare className="w-12 h-12 text-[#8C8078] mx-auto mb-4" />
          <h4 className="font-medium text-[#2C2420] mb-1">No {filter !== 'all' ? filter : ''} requests</h4>
          <p className="text-sm text-[#5D524D]">
            {filter === 'pending' 
              ? 'All caught up! No pending access requests.'
              : `No ${filter} access requests found.`}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {requests.map((request) => (
            <RequestCard
              key={request.id}
              request={request}
              onApprove={handleApprove}
              onDeny={handleDeny}
            />
          ))}
        </div>
      )}
    </div>
  );
}
